import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Plus, FileText, Building2, Calendar, Euro, Search, Copy, Trash2, ChevronsUpDown, X } from "lucide-react";
import { toast } from "sonner";
import GroupSortControls from "@/components/GroupSortControls";
import useResizableColumns from "@/hooks/useResizableColumns";
import { fmtEUR } from "@/lib/format";

const COLS_DEFAULT_WIDTHS = [110, 280, 240, 110, 110, 130, 130, 110]; // px
const COLS_STORAGE_KEY = "dnf-presupuestos-cols-v1";

const ESTADOS = {
  borrador:   { label: "Borrador",   color: "bg-zinc-700 text-zinc-200" },
  enviado:    { label: "Enviado",    color: "bg-blue-600/25 text-blue-200 border border-blue-500/40" },
  aceptado:   { label: "Aceptado",   color: "bg-emerald-600/25 text-emerald-200 border border-emerald-500/40" },
  rechazado:  { label: "Rechazado",  color: "bg-red-600/25 text-red-200 border border-red-500/40" },
  caducado:   { label: "Caducado",   color: "bg-zinc-600/25 text-zinc-300 border border-zinc-500/40" },
};

const fmtFecha = (iso) => iso ? new Date(iso).toLocaleDateString("es-ES") : "";

export default function PresupuestosList() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState("todos");
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [newCliente, setNewCliente] = useState("");
  const [openCliCombo, setOpenCliCombo] = useState(false);
  const [groupBy, setGroupBy] = useState(() => localStorage.getItem("dnf-pres-groupby") || "none");
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("dnf-pres-sortby") || "fecha");
  const [sortDir, setSortDir] = useState(() => localStorage.getItem("dnf-pres-sortdir") || "desc");
  useEffect(() => { localStorage.setItem("dnf-pres-groupby", groupBy); }, [groupBy]);
  useEffect(() => { localStorage.setItem("dnf-pres-sortby", sortBy); }, [sortBy]);
  useEffect(() => { localStorage.setItem("dnf-pres-sortdir", sortDir); }, [sortDir]);
  const { widths, startResize, resetWidths } = useResizableColumns(
    COLS_STORAGE_KEY, COLS_DEFAULT_WIDTHS
  );

  const load = async () => {
    setLoading(true);
    try {
      const [pres, cli] = await Promise.all([
        api.get("/presupuestos"),
        api.get("/clientes"),
      ]);
      setItems(pres.data);
      setClientes(cli.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtrados = useMemo(() => {
    const base = items.filter((p) => {
      if (estado !== "todos" && p.estado !== estado) return false;
      if (q) {
        const t = q.toLowerCase();
        const cli = (p.cliente_snapshot?.razon_social || "").toLowerCase();
        const num = (p.numero || "").toLowerCase();
        const ref = (p.referencia || "").toLowerCase();
        if (!cli.includes(t) && !num.includes(t) && !ref.includes(t)) return false;
      }
      return true;
    });
    base.sort((a, b) => {
      let va, vb;
      switch (sortBy) {
        case "fecha":     va = a.fecha || ""; vb = b.fecha || ""; break;
        case "total":     va = a.totales?.total || 0; vb = b.totales?.total || 0; break;
        case "numero":    va = a.numero || ""; vb = b.numero || ""; break;
        case "referencia":va = (a.referencia || "").toLowerCase(); vb = (b.referencia || "").toLowerCase(); break;
        case "cliente":   va = (a.cliente_snapshot?.razon_social || "").toLowerCase();
                          vb = (b.cliente_snapshot?.razon_social || "").toLowerCase(); break;
        case "estado":    va = a.estado || ""; vb = b.estado || ""; break;
        case "validez":   va = a.fecha_validez || ""; vb = b.fecha_validez || ""; break;
        default:          va = a.fecha || ""; vb = b.fecha || "";
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return base;
  }, [items, estado, q, sortBy, sortDir]);

  const grouped = useMemo(() => {
    if (groupBy === "none") return [{ key: null, items: filtrados }];
    const keyOf = (p) => {
      switch (groupBy) {
        case "estado": return (ESTADOS[p.estado]?.label) || (p.estado || "Sin estado");
        case "cliente": return p.cliente_snapshot?.razon_social || "Sin cliente";
        case "mes":
          if (!p.fecha) return "Sin fecha";
          try {
            const d = new Date(p.fecha);
            return d.toLocaleDateString("es-ES", { year: "numeric", month: "long" });
          } catch (_e) { return "Sin fecha"; }
        default: return "—";
      }
    };
    const map = new Map();
    for (const p of filtrados) {
      const k = keyOf(p);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    }
    return Array.from(map.entries()).sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([key, items]) => ({ key, items }));
  }, [filtrados, groupBy]);

  const counts = useMemo(() => {
    const c = { borrador: 0, enviado: 0, aceptado: 0, rechazado: 0, caducado: 0 };
    items.forEach((p) => { if (c[p.estado] !== undefined) c[p.estado]++; });
    return c;
  }, [items]);

  const crear = async () => {
    try {
      const body = newCliente ? { cliente_id: newCliente } : {};
      const { data } = await api.post("/presupuestos", body);
      toast.success(`Presupuesto ${data.numero} creado`);
      setOpenNew(false);
      setNewCliente("");
      nav(`/presupuestos/${data.id}`);
    } catch (e) {
      toast.error("No se ha podido crear el presupuesto");
    }
  };

  const duplicar = async (pid) => {
    try {
      const { data } = await api.post(`/presupuestos/${pid}/duplicar`);
      toast.success(`Duplicado como ${data.numero}`);
      load();
    } catch (e) {
      toast.error("No se ha podido duplicar");
    }
  };

  const borrar = async (pid, numero) => {
    if (!window.confirm(`¿Borrar el presupuesto ${numero}? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/presupuestos/${pid}`);
      toast.success("Presupuesto borrado");
      load();
    } catch (e) {
      toast.error("No se ha podido borrar");
    }
  };

  return (
    <div className="p-6" data-testid="presupuestos-list">
      <PageHeader
        title="Presupuestos"
        subtitle="Crea, gestiona y envía presupuestos por WhatsApp o email."
        action={
          <Button data-testid="btn-nuevo-presupuesto" onClick={() => setOpenNew(true)}
            className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Nuevo presupuesto
          </Button>
        }
      />

      {/* Tiles de estado */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {Object.entries(ESTADOS).map(([k, cfg]) => (
          <button key={k}
            data-testid={`tile-estado-${k}`}
            onClick={() => setEstado(estado === k ? "todos" : k)}
            className={`p-3 rounded-xl border text-left transition-all ${
              estado === k
                ? "border-amber-500/60 bg-amber-500/10"
                : "border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900/70"
            }`}>
            <div className="text-2xl font-bold text-zinc-100">{counts[k] || 0}</div>
            <div className="text-[11px] uppercase tracking-wider text-zinc-400 mt-0.5">{cfg.label}</div>
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="input-buscar-presupuesto"
            placeholder="Buscar por nº, cliente o referencia…"
            className="pl-9 bg-zinc-900/60 border-zinc-800" />
        </div>
        {estado !== "todos" && (
          <Button variant="ghost" size="sm" onClick={() => setEstado("todos")}
            data-testid="btn-clear-filter-estado">Limpiar filtro</Button>
        )}
      </div>

      {/* Agrupar / Ordenar */}
      <div className="flex items-center justify-between mb-3">
        <GroupSortControls
          testIdPrefix="presupuestos"
          groupBy={groupBy} setGroupBy={setGroupBy}
          groupOptions={[
            { value: "estado", label: "Por estado" },
            { value: "cliente", label: "Por cliente" },
            { value: "mes", label: "Por mes (fecha)" },
          ]}
          sortBy={sortBy} setSortBy={setSortBy}
          sortOptions={[
            { value: "fecha", label: "Fecha" },
            { value: "total", label: "Importe total" },
            { value: "numero", label: "Número" },
            { value: "cliente", label: "Cliente (A-Z)" },
            { value: "estado", label: "Estado" },
            { value: "validez", label: "Validez" },
          ]}
          sortDir={sortDir} setSortDir={setSortDir}
          onReset={() => { setGroupBy("none"); setSortBy("fecha"); setSortDir("desc"); resetWidths(); }}
        />
        <div className="text-[10px] text-zinc-500" data-testid="presupuestos-count">
          {filtrados.length} {filtrados.length === 1 ? "presupuesto" : "presupuestos"}
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-x-auto">
        <table className="text-sm" style={{ tableLayout: "fixed", width: widths.reduce((s, w) => s + w, 0) + "px" }}>
          <colgroup>
            {widths.map((w, i) => <col key={i} style={{ width: `${w}px` }} />)}
          </colgroup>
          <thead className="bg-zinc-950/60">
            <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-400">
              {[
                { label: "Nº",         field: "numero" },
                { label: "Cliente",    field: "cliente" },
                { label: "Referencia", field: "referencia" },
                { label: "Fecha",      field: "fecha" },
                { label: "Validez",    field: "validez" },
                { label: "Total",      field: "total" },
                { label: "Estado",     field: "estado" },
                { label: "Acciones",   field: null },
              ].map(({ label, field }, i) => {
                const sortable = !!field;
                const active = sortable && sortBy === field;
                const onSort = () => {
                  if (!sortable) return;
                  if (sortBy !== field) { setSortBy(field); setSortDir("asc"); return; }
                  setSortDir(sortDir === "asc" ? "desc" : "asc");
                };
                return (
                  <th key={label}
                      className={`px-4 py-3 relative ${i === 5 ? "text-right" : ""} ${i === 7 ? "text-right" : ""} ${sortable ? "cursor-pointer hover:bg-zinc-800/40 select-none" : ""} ${active ? "text-amber-300" : ""}`}
                      onClick={onSort}
                      data-testid={sortable ? `th-pres-${field}` : undefined}>
                    <span className="truncate pr-2 inline-flex items-center gap-1">
                      {label}
                      {sortable && (
                        <span className={`text-[9px] ${active ? "opacity-100" : "opacity-30"}`}>
                          {!active ? "↕" : (sortDir === "asc" ? "▲" : "▼")}
                        </span>
                      )}
                    </span>
                    {i < 7 && (
                      <span
                        onMouseDown={startResize(i)}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={resetWidths}
                        data-testid={`presupuestos-col-resize-${i}`}
                        title="Arrastra para redimensionar · Doble-click reset"
                        className="absolute right-0 top-0 h-full w-2 -mr-1 cursor-col-resize hover:bg-amber-500/40 active:bg-amber-500/70 transition-colors z-10"
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">Cargando…</td></tr>
            )}
            {!loading && filtrados.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-zinc-500">
                {items.length === 0 ? "Aún no hay presupuestos. Crea el primero arriba ▲" : "Sin resultados con esos filtros."}
              </td></tr>
            )}
            {grouped.map((grp, gi) => (
              <React.Fragment key={grp.key ?? `grp-${gi}`}>
                {grp.key !== null && (
                  <tr data-testid={`presupuestos-group-${grp.key}`} className="bg-zinc-950/40 border-t border-zinc-800">
                    <td colSpan={8} className="px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-amber-300/80 font-semibold">
                      {grp.key} <span className="text-zinc-500 ml-2">({grp.items.length})</span>
                    </td>
                  </tr>
                )}
                {grp.items.map((p) => {
                  const cfg = ESTADOS[p.estado] || ESTADOS.borrador;
                  return (
                    <tr key={p.id} data-testid={`row-pres-${p.id}`}
                        className="border-t border-zinc-800/70 hover:bg-zinc-900/40 cursor-pointer"
                        onClick={() => nav(`/presupuestos/${p.id}`)}>
                      <td className="px-4 py-3 font-mono text-amber-300 truncate">{p.numero}</td>
                      <td className="px-4 py-3 text-zinc-100 truncate">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                          <span className="truncate">{p.cliente_snapshot?.razon_social || <span className="italic text-zinc-500">Sin cliente</span>}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-zinc-300 truncate" title={p.referencia || ""}
                          data-testid={`row-ref-${p.id}`}>
                        {p.referencia || <span className="italic text-zinc-600">—</span>}
                      </td>
                      <td className="px-4 py-3 text-zinc-300 truncate">{fmtFecha(p.fecha)}</td>
                      <td className="px-4 py-3 text-zinc-400 truncate">{fmtFecha(p.fecha_validez)}</td>
                      <td className="px-4 py-3 text-right text-zinc-100 font-semibold truncate">{fmtEUR(p.totales?.total)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${cfg.color}`}>{cfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" variant="ghost" onClick={() => duplicar(p.id)}
                          data-testid={`btn-duplicar-${p.id}`}
                          className="h-7 px-2 text-zinc-400 hover:text-amber-300">
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => borrar(p.id, p.numero)}
                          data-testid={`btn-borrar-${p.id}`}
                          className="h-7 px-2 text-zinc-400 hover:text-red-400">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Dialog nuevo presupuesto */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="dialog-nuevo-presupuesto">
          <DialogHeader>
            <DialogTitle className="text-amber-300">Nuevo presupuesto</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs text-zinc-400">Cliente (opcional, puedes asignarlo después)</Label>
            <Popover open={openCliCombo} onOpenChange={setOpenCliCombo}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={openCliCombo}
                  data-testid="select-cliente-nuevo"
                  className="w-full bg-zinc-900 border-zinc-800 justify-between font-normal text-zinc-100 hover:bg-zinc-900 hover:text-zinc-100"
                >
                  <span className="truncate">
                    {newCliente
                      ? (clientes.find((c) => c.id === newCliente)?.nombre || "Cliente seleccionado")
                      : <span className="text-zinc-500">Buscar cliente por nombre o CIF…</span>}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-zinc-950 border-zinc-800" align="start">
                <Command className="bg-zinc-950">
                  <CommandInput placeholder="Escribe para buscar…" className="text-zinc-100" />
                  <CommandList className="max-h-72">
                    <CommandEmpty className="text-zinc-500 text-xs py-4 text-center">Sin resultados</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="__none__"
                        onSelect={() => { setNewCliente(""); setOpenCliCombo(false); }}
                        className="text-zinc-400 italic"
                      >
                        <X className="w-3.5 h-3.5 mr-2 opacity-60" />
                        Sin cliente (lo añado luego)
                      </CommandItem>
                      {clientes.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.nombre} ${c.cif || ""} ${c.email_contacto || ""}`}
                          onSelect={() => { setNewCliente(c.id); setOpenCliCombo(false); }}
                          data-testid={`cliente-option-${c.id}`}
                          className="text-zinc-100"
                        >
                          <Building2 className="w-3.5 h-3.5 mr-2 text-amber-400 opacity-70" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{c.nombre}</div>
                            {c.cif && <div className="text-[10px] text-zinc-500">{c.cif}</div>}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <div className="text-[11px] text-zinc-500">
              El número se asigna automáticamente (PRES-2026-XXXX). Después podrás añadir conceptos del catálogo o crear items externos con proveedor.
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpenNew(false)} data-testid="btn-cancel-nuevo">Cancelar</Button>
            <Button onClick={crear} data-testid="btn-confirm-nuevo"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              <FileText className="w-4 h-4 mr-2" /> Crear y editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
